import React, { useState, useRef, useEffect } from 'react';
import AudioPlayer from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import styled from 'styled-components';
import { useAudio } from '../../contexts/AudioContext';

interface Track {
    title: { type: 'text'; content: string }[];
    artist: { type: 'text'; content: string }[];
    album: { type: 'text'; content: string }[];
    duration: string;
    src: string;
}

interface ListInfo {
    type: 'album';
    name: string;
    numTracks: number;
    duration: string;
    releaseDate?: string;
    genre?: string;
    imageSrc?: string;
}

interface AlbumPlayerProps {
    albumId: string;
    tracks: Track[];
    listInfo: ListInfo;
}

const PlayerContainer = styled.div`
    background-color: ${({ theme }) => theme.colors.backgroundDarker};
    border-radius: ${({ theme }) => theme.borderRadius.large};
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const AlbumHeader = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.lg};
    padding: ${({ theme }) => theme.spacing.xl};
    background-color: ${({ theme }) => theme.colors.backgroundDark};

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        padding: ${({ theme }) => theme.spacing.md};
        gap: ${({ theme }) => theme.spacing.md};
    }
`;

const AlbumArtwork = styled.img`
    width: 160px;
    height: 160px;
    border-radius: ${({ theme }) => theme.borderRadius.medium};
    object-fit: cover;
    flex-shrink: 0;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        width: 120px;
        height: 120px;
    }
`;

const AlbumInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const AlbumTitle = styled.h2`
    font-size: 2rem;
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    color: ${({ theme }) => theme.colors.text};
    margin: 0;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: 1.5rem;
    }
`;

const AlbumDetail = styled.div`
    font-size: 1rem;
    color: ${({ theme }) => theme.colors.textMuted};
    line-height: 1.4;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: 0.875rem;
    }
`;

const TrackList = styled.div`
    padding: 0 0 ${({ theme }) => theme.spacing.sm} 0;
`;

const TrackItem = styled.div<{ $isActive: boolean }>`
    display: grid;
    grid-template-columns: 40px 1fr auto;
    gap: ${({ theme }) => theme.spacing.sm};
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
    cursor: pointer;
    transition: background-color 0.2s;
    background-color: ${({ $isActive, theme }) =>
        $isActive ? theme.colors.backgroundLighter : 'transparent'};
    position: relative;

    /* Top gradient line */
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: linear-gradient(
            to right,
            transparent 0%,
            ${({ theme }) => theme.colors.primary} 50%,
            transparent 100%
        );
        opacity: 0.3;
    }

    /* Bottom gradient line for last item */
    &:last-child::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: linear-gradient(
            to right,
            transparent 0%,
            ${({ theme }) => theme.colors.primary} 50%,
            transparent 100%
        );
        opacity: 0.3;
    }

    &:hover {
        background-color: ${({ theme }) => theme.colors.backgroundLighter};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        grid-template-columns: 30px 1fr auto;
        padding: 0.5rem ${({ theme }) => theme.spacing.md};
        gap: ${({ theme }) => theme.spacing.sm};
    }
`;

const TrackNumber = styled.div<{ $isActive: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${({ theme }) => theme.fontSizes.small};
    color: ${({ $isActive, theme }) => ($isActive ? theme.colors.primary : theme.colors.textMuted)};
    font-weight: ${({ $isActive, theme }) =>
        $isActive ? theme.fontWeights.bold : theme.fontWeights.normal};

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: 0.875rem;
    }
`;

const TrackInfo = styled.div`
    display: flex;
    align-items: center;
    min-width: 0;
`;

const TrackTitle = styled.div<{ $isActive: boolean }>`
    font-size: 1rem;
    color: ${({ $isActive, theme }) => ($isActive ? theme.colors.primary : theme.colors.text)};
    font-weight: ${({ $isActive, theme }) =>
        $isActive ? theme.fontWeights.bold : theme.fontWeights.normal};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: 0.875rem;
    }
`;

const TrackDuration = styled.div`
    display: flex;
    align-items: center;
    font-size: 0.875rem;
    color: ${({ theme }) => theme.colors.textMuted};

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: 0.75rem;
    }
`;

const AudioPlayerWrapper = styled.div`
    padding: 2rem;
    background-color: ${({ theme }) => theme.colors.backgroundDark};

    /* Base player styles */
    .rhap_container {
        background-color: transparent;
        box-shadow: none;
        padding: 0;
    }

    .rhap_time {
        color: ${({ theme }) => theme.colors.textMuted};
        font-size: 0.875rem;
    }

    .rhap_progress-bar {
        background-color: ${({ theme }) => theme.colors.backgroundLighter};
        height: 6px;
    }

    .rhap_progress-filled,
    .rhap_progress-indicator {
        background-color: ${({ theme }) => theme.colors.primary};
    }

    .rhap_button-clear {
        color: ${({ theme }) => theme.colors.text};
    }

    .rhap_button-clear:hover {
        color: ${({ theme }) => theme.colors.primary};
    }

    .rhap_volume-bar {
        background-color: ${({ theme }) => theme.colors.backgroundLighter};
    }

    .rhap_volume-indicator {
        background-color: ${({ theme }) => theme.colors.primary};
    }

    /* Mobile styles */
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        padding: 1.5rem 1rem;

        .rhap_controls-section {
            justify-content: center;
        }

        .rhap_main-controls {
            justify-content: center;
        }

        .rhap_additional-controls,
        .rhap_volume-controls {
            display: none;
        }

        .rhap_time {
            font-size: 0.75rem;
        }
    }
`;

const getTrackText = (textArray: { type: 'text'; content: string }[]) => {
    return textArray.map((item) => item.content).join(' ');
};

export const AlbumPlayer: React.FC<AlbumPlayerProps> = ({ albumId, tracks, listInfo }) => {
    const { currentPlaylist, setCurrentPlaylist, selectedTrack, setSelectedTrack } = useAudio();
    const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
    const audioRef = useRef<AudioPlayer>(null);

    const isActivePlaylist = currentPlaylist === albumId;

    useEffect(() => {
        if (isActivePlaylist && selectedTrack !== currentTrackIndex) {
            setCurrentTrackIndex(selectedTrack);
        }
    }, [isActivePlaylist, selectedTrack, currentTrackIndex]);

    const handleTrackClick = (index: number) => {
        setCurrentPlaylist(albumId);
        setSelectedTrack(index);
        setCurrentTrackIndex(index);
    };

    const handleClickNext = () => {
        const nextIndex = currentTrackIndex < tracks.length - 1 ? currentTrackIndex + 1 : 0;
        handleTrackClick(nextIndex);
    };

    const handleClickPrevious = () => {
        const prevIndex = currentTrackIndex > 0 ? currentTrackIndex - 1 : tracks.length - 1;
        handleTrackClick(prevIndex);
    };

    const handleEnded = () => {
        if (currentTrackIndex < tracks.length - 1) {
            handleClickNext();
        }
    };

    const currentTrack = tracks[currentTrackIndex];

    return (
        <PlayerContainer>
            <AlbumHeader>
                {listInfo.imageSrc && <AlbumArtwork src={listInfo.imageSrc} alt={listInfo.name} />}
                <AlbumInfo>
                    <AlbumTitle>{listInfo.name}</AlbumTitle>
                    <AlbumDetail>The Butterscotch Boys</AlbumDetail>
                    {listInfo.releaseDate && <AlbumDetail>{listInfo.releaseDate}</AlbumDetail>}
                </AlbumInfo>
            </AlbumHeader>

            <AudioPlayerWrapper>
                <AudioPlayer
                    ref={audioRef}
                    src={currentTrack?.src}
                    showSkipControls
                    showJumpControls={false}
                    onClickNext={handleClickNext}
                    onClickPrevious={handleClickPrevious}
                    onEnded={handleEnded}
                    autoPlayAfterSrcChange={isActivePlaylist}
                />
            </AudioPlayerWrapper>

            <TrackList>
                {tracks.map((track, index) => (
                    <TrackItem
                        key={index}
                        $isActive={isActivePlaylist && index === currentTrackIndex}
                        onClick={() => handleTrackClick(index)}
                    >
                        <TrackNumber $isActive={isActivePlaylist && index === currentTrackIndex}>
                            {index + 1}
                        </TrackNumber>
                        <TrackInfo>
                            <TrackTitle $isActive={isActivePlaylist && index === currentTrackIndex}>
                                {getTrackText(track.title)}
                            </TrackTitle>
                        </TrackInfo>
                        <TrackDuration>{track.duration}</TrackDuration>
                    </TrackItem>
                ))}
            </TrackList>
        </PlayerContainer>
    );
};
